<script setup lang="ts">
/**
 * StatsPanel — Complete stats display with all sub-sections
 * Shows resources, defense, offense, capacitor, navigation, targeting.
 * Has scroll fade indicators at top/bottom.
 */
import { ref, onMounted, onUnmounted } from 'vue';
import type { FitStats } from '../../../shared/types';
import ResourceBars from './ResourceBars.vue';
import DefenseStats from './DefenseStats.vue';
import OffenseStats from './OffenseStats.vue';
import CapacitorDisplay from './CapacitorDisplay.vue';
import NavigationStats from './NavigationStats.vue';
import TargetingStats from './TargetingStats.vue';

const props = defineProps<{
  stats: FitStats;
}>();

// Scroll fade
const scrollEl = ref<HTMLElement | null>(null);
const canScrollUp = ref(false);
const canScrollDown = ref(false);

function checkScroll(): void {
  const el = scrollEl.value;
  if (!el) return;
  canScrollUp.value = el.scrollTop > 4;
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
}

onMounted(() => {
  const el = scrollEl.value;
  if (el) {
    el.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
  }
});

onUnmounted(() => {
  scrollEl.value?.removeEventListener('scroll', checkScroll);
});
</script>

<template>
  <div class="stats-panel">
    <div class="scroll-fade-top" v-if="canScrollUp"></div>

    <div ref="scrollEl" class="stats-scroll">
      <!-- Resources -->
      <section class="stat-section">
        <h3 class="section-title">Resources</h3>
        <ResourceBars
          :cpu="props.stats.resources.cpu"
          :power-grid="props.stats.resources.powerGrid"
          :calibration="props.stats.resources.calibration"
          :slots="props.stats.resources.slots"
        />
      </section>

      <!-- Defense -->
      <section class="stat-section">
        <h3 class="section-title">Defense</h3>
        <DefenseStats :defense="props.stats.defense" />
      </section>

      <!-- Offense -->
      <section class="stat-section">
        <h3 class="section-title">Offense</h3>
        <OffenseStats :offense="props.stats.offense" />
      </section>

      <!-- Capacitor -->
      <section class="stat-section">
        <h3 class="section-title">Capacitor</h3>
        <CapacitorDisplay :capacitor="props.stats.capacitor" />
      </section>

      <!-- Navigation -->
      <section class="stat-section">
        <h3 class="section-title">Navigation</h3>
        <NavigationStats :navigation="props.stats.navigation" />
      </section>

      <!-- Targeting -->
      <section class="stat-section">
        <h3 class="section-title">Targeting</h3>
        <TargetingStats :targeting="props.stats.targeting" />
      </section>
    </div>

    <div class="scroll-fade-bottom" v-if="canScrollDown"></div>
  </div>
</template>

<style scoped>
.stats-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  position: relative;
}

.stats-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 10px 12px 16px;
}

.stat-section {
  padding: 10px;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(30, 42, 61, 0.72);
  border-radius: var(--radius-md);
}

.stat-section:last-child {
  margin-bottom: 0;
}

.section-title {
  margin: 0 0 7px;
  color: var(--accent-blue);
  font-family: var(--font-mono);
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.scroll-fade-top,
.scroll-fade-bottom {
  position: absolute;
  left: 0;
  right: 0;
  height: 16px;
  pointer-events: none;
  z-index: 1;
}

.scroll-fade-top {
  top: 0;
  background: linear-gradient(to bottom, var(--bg-panel), transparent);
}

.scroll-fade-bottom {
  bottom: 0;
  background: linear-gradient(to top, var(--bg-panel), transparent);
}
</style>
